import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Badge, { BADGE_VARIANTS } from '../../../components/ui/Badge';
import PortalDropdown from '../../../components/ui/PortalDropdown';
import { statusVariant } from '../../../utils/statusVariant';

/**
 * Pastille de statut d'un projet qui sert de menu, dans la liste des projets :
 * un clic ouvre les statuts juste en dessous, avec une coche sur le statut
 * courant. Menu LOCAL a cette page : PortalDropdown (partage) n'est utilise
 * qu'en mode « contenu libre », pour la position, Echap et le clic dehors.
 *
 * Props:
 *   status    string   — statut courant du projet (valeur de la base)
 *   options   string[] — statuts offerts
 *   saving    boolean  — une ecriture est en cours pour ce projet : pastille
 *                        attenuee, aucun autre changement possible
 *   label     (status) => string — libelle traduit d'un statut
 *   onChange  (status) => void
 */
const ProjectStatusMenu = ({ status, options, saving = false, label, onChange }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const getItems = () => [...(listRef?.current?.querySelectorAll('[role="menuitemradio"]') ?? [])];

  // A l'ouverture : le curseur va sur le statut courant, pour les fleches.
  useEffect(() => {
    if (!open) return;
    const items = getItems();
    (items?.find((item) => item?.getAttribute('aria-checked') === 'true') ?? items?.[0])?.focus({ preventScroll: true });
  }, [open]);

  // Fermeture (Echap, clic dehors, defilement) : le curseur revient sur la pastille s'il etait dans le menu.
  const close = () => {
    if (listRef?.current?.contains(document.activeElement)) buttonRef?.current?.focus();
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    const items = getItems();
    const index = items?.indexOf(document.activeElement);
    if (e?.key === 'ArrowDown' || e?.key === 'ArrowUp') {
      e?.preventDefault();
      items?.[(index + (e?.key === 'ArrowDown' ? 1 : items?.length - 1)) % items?.length]?.focus();
    } else if (e?.key === 'Home' || e?.key === 'End') {
      e?.preventDefault();
      items?.[e?.key === 'Home' ? 0 : items?.length - 1]?.focus();
    } else if (e?.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <>
      <Badge
        as="button"
        type="button"
        ref={buttonRef}
        variant={statusVariant('project', status)}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={saving}
        onClick={(e) => {
          // Le clic ne remonte pas a la rangee : il n'ouvre pas le projet.
          e?.stopPropagation();
          setOpen((current) => !current);
        }}
        onKeyDown={(e) => {
          if (!open && (e?.key === 'ArrowDown' || e?.key === 'ArrowUp')) {
            e?.preventDefault();
            setOpen(true);
          }
        }}
        className={`transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 ${saving ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:opacity-80'}`}
      >
        {label(status)}
        <Icon name="ChevronDown" size={12} className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </Badge>
      <PortalDropdown anchorRef={buttonRef} open={open} onClose={close} width={170} align="left">
        <div ref={listRef} role="menu" className="px-1" onKeyDown={handleKeyDown} onClick={(e) => e?.stopPropagation()}>
          {options?.map((option) => (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={option === status}
              onClick={(e) => {
                e?.stopPropagation();
                close();
                if (option !== status) onChange(option);
              }}
              className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs text-left text-gray-700 whitespace-nowrap cursor-pointer hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none"
            >
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: BADGE_VARIANTS?.[statusVariant('project', option)]?.dot }} />
              <span className={option === status ? 'font-semibold' : ''}>{label(option)}</span>
              {option === status && <Icon name="Check" size={13} className="ml-auto text-emerald-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      </PortalDropdown>
    </>
  );
};

export default ProjectStatusMenu;
